import { Trans } from '@lingui/macro'
import { Box, Breadcrumbs, Button } from '@mui/material'
import {
    CohortRoutes,
    PlatformRoute,
    createCohortDashboardPageComponent,
    createCohortEditComponent,
    createCohortListComponent,
    createCreateCohortWizardDialogComponent,
    createDatasetsListComponent,
    createReportsPage,
    toRoutingConfigObject
} from '@om1/cohort-module'
import { createReadOnlyCohortPageComponent } from '@om1/cohort-module/src/components/reports/ReadOnlyCohortPage'
import { createSummaryReportPageComponent } from '@om1/cohort-module/src/components/reports/SummaryReportPage'
import { createCohortQueryExplorer } from '@om1/platform-assistant-ui/src/components/CohortQueryExplorer'
import { createEditCohortConversationComponent } from '@om1/platform-assistant-ui/src/components/EditCohortConversation'
import { ExplorerLayout } from '@om1/platform-assistant-ui/src/layouts/ExplorerLayout'
import { Routes } from '@om1/platform-routing'
import { FilterTabBar, FilterTabBarDivider } from '@om1/platform-ui-kit/src/components/FilterTabBar'
import { BreadcrumbLink, ContentContainerStyle, ContentPadding, InvertContentPadding } from '@om1/platform-ui-kit/src/components/Layout'
import { PlatformPermissions } from '@om1/platform-utils'
import { FunctionComponent } from 'react'
import { Link, Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'
import { PlatformState } from '../../platform-state'

export function isCohortBuilderPrintViewUrl(url: string) {
    const regex = /^\/cohort-builder\/print\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i
    return regex.test(url)
}

const CohortEdit = createCohortEditComponent<PlatformState>()
const CohortList = createCohortListComponent<PlatformState>()
const CohortDashboardPage = createCohortDashboardPageComponent<PlatformState>()
const DatasetsList = createDatasetsListComponent<PlatformState>()
const ReportsPage = createReportsPage<PlatformState>()
const ReadOnlyCohortPage = createReadOnlyCohortPageComponent<PlatformState>()
const SummaryReportPage = createSummaryReportPageComponent<PlatformState>()
const CreateCohortWizardComponent = createCreateCohortWizardDialogComponent<PlatformState>()
const EditCohortConversation = createEditCohortConversationComponent()
const CohortQueryExplorer = createCohortQueryExplorer()

export const CohortPage: FunctionComponent = () => {
    const match = useRouteMatch()
    const location = useLocation()

    if (!match) {
        return null
    }

    const { path: matchPathValue, url: matchUrlValue } = match

    const routes: PlatformRoute[] = [
        {
            route: `${CohortRoutes.DASHBOARD}/:cohortOwner?/:cohortId?/:reportType?`,
            component: CohortDashboardPage,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.INDEX_COHORTS]
        },
        {
            route: `${CohortRoutes.DASHBOARD}/`,
            component: CohortDashboardPage,
            permissions: [PlatformPermissions.ADMIN_EXPLORER, PlatformPermissions.INDEX_COHORTS]
        },
        {
            route: `${CohortRoutes.PRINT}/:cohortId`,
            component: SummaryReportPage,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.GET_COHORT, PlatformPermissions.ACCESS_SUMMARY_REPORTS_FEATURES]
        },
        {
            route: CohortRoutes.CUSTOM,
            component: CohortList,
            name: <Trans>Custom Cohorts</Trans>,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.INDEX_COHORTS]
        },
        {
            route: `${CohortRoutes.CUSTOM}/:cohortId/${CohortRoutes.EDIT}`,
            component: CohortEdit,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.UPDATE_COHORT]
        },
        {
            route: `${CohortRoutes.CUSTOM}/:cohortId/${CohortRoutes.CHAT}`,
            component: () => (
                <InvertContentPadding style={{ marginLeft: '-20px', marginRight: '-60px' }}>
                    <EditCohortConversation />
                </InvertContentPadding>
            ),
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.ACCESS_ASSISTANT]
        },
        {
            route: `${CohortRoutes.CUSTOM}/:cohortId/${CohortRoutes.EXPLORER}`,
            component: () => (
                <InvertContentPadding style={{ marginLeft: '-20px', marginRight: '-60px' }}>
                    <CohortQueryExplorer />
                </InvertContentPadding>
            ),
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.ACCESS_ASSISTANT]
        },
        {
            route: `${CohortRoutes.CUSTOM}/:cohortId`,
            component: ReadOnlyCohortPage,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.GET_COHORT]
        },
        {
            route: `${CohortRoutes.DATASETS}/:cohortId`,
            component: ReadOnlyCohortPage,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.GET_COHORT]
        },
        {
            route: CohortRoutes.DATASETS,
            component: DatasetsList,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.INDEX_COHORTS],
            name: <Trans>Datasets</Trans>
        },
        {
            route: CohortRoutes.WIZARD,
            component: CreateCohortWizardComponent,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.CREATE_COHORT]
        },
        {
            route: `${CohortRoutes.REPORTS}/:cohortId/:reportType?`,
            component: ReportsPage,
            permissions: [PlatformPermissions.ACCESS_COHORTS, PlatformPermissions.INDEX_COHORTS]
        },
        {
            route: `${CohortRoutes.ASSISTANT}/*`,
            component: () => (
                <InvertContentPadding style={{ marginLeft: '-36px', marginRight: '-36px', marginTop: '-16px', marginBottom: '-16px' }}>
                    <ExplorerLayout />
                </InvertContentPadding>
            ),
            permissions: [PlatformPermissions.ACCESS_ASSISTANT]
        }
    ]

    const routingConfigObject = toRoutingConfigObject(routes, matchPathValue, matchUrlValue)

    function isCohortBuilderDashboardUrl(url: string) {
        return url.includes('dashboard')
    }

    function isCohortBuilderEditUrl(url: string) {
        return url.includes('edit')
    }

    function isCohortBuilderChatUrl(url: string) {
        return url.includes('chat')
    }

    function isCohortBuilderExplorerUrl(url: string) {
        return url.includes('explorer')
    }

    function isCohortBuilderAssistantUrl(url: string) {
        return url.includes('assistant')
    }

    function isCohortViewUrl(url: string) {
        const regex = /^\/cohort-builder\/custom\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        return regex.test(url)
    }

    function isCohortReportsViewUrl(url: string) {
        const regex = /^\/cohort-builder\/reports\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/.+$/i
        return regex.test(url)
    }

    const tabs = !isCohortBuilderDashboardUrl(location.pathname) &&
        !isCohortBuilderEditUrl(location.pathname) &&
        !isCohortViewUrl(location.pathname) &&
        !isCohortReportsViewUrl(location.pathname) &&
        !isCohortBuilderPrintViewUrl(location.pathname) &&
        !isCohortBuilderExplorerUrl(location.pathname) &&
        !isCohortBuilderChatUrl(location.pathname) &&
        !isCohortBuilderAssistantUrl(location.pathname) && (
            <FilterTabBar>
                {routingConfigObject.map((tab) => {
                    const base = tab.url.split('/')[2]
                    return tab.name ? (
                        <div key={`tabbuttondiv-${tab.route}`}>
                            <Button
                                key={`tabbutton-${tab.route}`}
                                component={Link}
                                to={tab.url}
                                aria-current={location.pathname.includes(base) ? 'true' : 'false'}
                                sx={{
                                    minWidth: 128,
                                    borderRadius: 0,
                                    borderBottomWidth: 3,
                                    borderBottomStyle: 'solid',
                                    borderBottomColor: location.pathname.includes(base) ? 'primary.main' : 'transparent',
                                    '&:hover': {
                                        borderBottomColor: location.pathname.includes(base) ? 'primary.main' : 'secondary.main'
                                    }
                                }}
                            >
                                {tab.name}
                            </Button>
                            <FilterTabBarDivider />
                        </div>
                    ) : null
                })}
            </FilterTabBar>
        )

    const pages = (
        <Switch>
            {routingConfigObject.map((tab) => (
                <Route key={`route-${tab.name}`} {...tab.matchRoute} exact={true} component={tab.component} />
            ))}
            <Redirect key='cohortRedirect' to={routingConfigObject[1].url} />
        </Switch>
    )

    let content = pages
    if (!isCohortBuilderDashboardUrl(location.pathname)) {
        content = (
            <ContentPadding padX padY>
                {content}
            </ContentPadding>
        )
    }

    // Breadcrumb logic
    const getBreadcrumbs = () => {
        const pathname = location.pathname

        // Check current route type
        const isDashboard = pathname.includes('/dashboard')
        const isEdit = pathname.includes('/edit')
        const isChat = pathname.includes('/chat')
        const isExplorer = pathname.includes('/explorer')
        const isReports = pathname.includes('/reports')
        const isPrint = pathname.includes('/print')
        const isAssistant = pathname.includes('/assistant')

        // Don't show breadcrumbs on edit, reports, or AI assistant pages
        if (isEdit || isReports || isChat || isExplorer || isAssistant) {
            return null
        }

        // Add padding above and below breadcrumbs for better spacing
        const topMargin = '16px'

        return (
            <Box sx={{ mt: topMargin, ml: 3, mb: 2 }}>
                <Breadcrumbs separator='›' aria-label='breadcrumb'>
                    <BreadcrumbLink to={`/${Routes.HOME}`} sx={{ fontWeight: '600', fontSize: '16px', color: '#012D72' }}>
                        <Trans>Home</Trans>
                    </BreadcrumbLink>

                    {/* Always show Cohort Builder as intermediate if not on dashboard */}
                    {!isDashboard ? (
                        <BreadcrumbLink to={`/${Routes.COHORTBUILD}/dashboard`} sx={{ fontWeight: '600', fontSize: '16px', color: '#012D72' }}>
                            <Trans>Cohort Explorer</Trans>
                        </BreadcrumbLink>
                    ) : (
                        <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                            <Trans>Cohort Explorer</Trans>
                        </Box>
                    )}

                    {(isEdit || isChat || isExplorer) && (
                        <>
                            <BreadcrumbLink to={`/${Routes.COHORTBUILD}/custom`} sx={{ fontWeight: '600', fontSize: '16px', color: '#012D72' }}>
                                <Trans>Custom Cohorts</Trans>
                            </BreadcrumbLink>
                            {isEdit && (
                                <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                                    <Trans>Edit</Trans>
                                </Box>
                            )}
                            {isChat && (
                                <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                                    <Trans>AI Assistant</Trans>
                                </Box>
                            )}
                            {isExplorer && (
                                <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                                    <Trans>Query Explorer</Trans>
                                </Box>
                            )}
                        </>
                    )}

                    {isReports && (
                        <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                            <Trans>Reports</Trans>
                        </Box>
                    )}

                    {isPrint && (
                        <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                            <Trans>Summary Report</Trans>
                        </Box>
                    )}

                    {isAssistant && (
                        <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                            <Trans>AI Assistant</Trans>
                        </Box>
                    )}
                </Breadcrumbs>
            </Box>
        )
    }

    return (
        <ContentContainerStyle>
            {getBreadcrumbs()}
            {tabs}
            {content}
        </ContentContainerStyle>
    )
}
